import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Card,
    Modal,
    Skeleton,
    Typography,
    type TypographyProps,
} from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BranchImports, ImportTotals } from './components';
import type { UseDoneStepResult } from './useDoneStep';

type DoneStepViewProps = UseDoneStepResult;

// The title is the step's only page-level heading.
const asH1: NonNullable<TypographyProps['render']> = ({
    children,
    ...domProps
}) => <h1 {...domProps}>{children}</h1>;

/**
 * Done: the title, the imports so far, Import more and Finish.
 *
 * The imports are listed by branch (`BranchImports`), each with its total,
 * then the totals across branches (`ImportTotals`); undone imports show
 * muted and count in neither. Skeletons while the history loads, a danger
 * notice with Retry when it fails, the empty state when there is none.
 * Import more and Finish work in every case.
 *
 * Finish asks first; a refusal stays in the dialog, inline, with Retry.
 */
export const DoneStepView: React.FC<DoneStepViewProps> = ({
    imports,
    isImportsLoading,
    isImportsError,
    onRetryImports,
    isNarrow,
    onImportMore,
    isConfirmOpen,
    onFinish,
    onCancelFinish,
    onConfirmFinish,
    isFinishing,
    finishError,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            className="flex flex-col gap-8 py-4 sm:py-8"
            {...getNodeTestIdProp('onboarding-done')}
        >
            <div className="flex flex-col gap-2">
                <Typography
                    render={asH1}
                    type="h2"
                    {...getNodeTestIdProp('onboarding-done-title')}
                >
                    {t('onboarding.done.title')}
                </Typography>
                <Typography.Paragraph color="muted">
                    {t('onboarding.done.intro')}
                </Typography.Paragraph>
            </div>
            {isImportsLoading ? (
                <div
                    className="flex flex-col gap-3"
                    {...getNodeTestIdProp('onboarding-done-loading')}
                >
                    <Skeleton className="h-6 w-48 rounded-lg" />
                    <Skeleton className="h-32 rounded-xl" />
                </div>
            ) : isImportsError ? (
                <Alert
                    status="danger"
                    {...getNodeTestIdProp('onboarding-done-load-error')}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Title>
                            {t('onboarding.done.imports.loadError')}
                        </Alert.Title>
                        <div className="pt-2">
                            <Button
                                onPress={onRetryImports}
                                size="sm"
                                variant="secondary"
                                {...getNodeTestIdProp(
                                    'onboarding-done-load-retry',
                                )}
                            >
                                {t('onboarding.done.imports.retry')}
                            </Button>
                        </div>
                    </Alert.Content>
                </Alert>
            ) : imports && imports.groups.length > 0 ? (
                <div
                    className="flex flex-col gap-8"
                    {...getNodeTestIdProp('onboarding-done-imports')}
                >
                    {imports.groups.map((group) => (
                        <BranchImports
                            group={group}
                            isNarrow={isNarrow}
                            key={group.branchId}
                        />
                    ))}
                    {imports.hidden > 0 ? (
                        <Typography
                            color="muted"
                            type="body-sm"
                            {...getNodeTestIdProp('onboarding-done-hidden')}
                        >
                            {t('onboarding.done.imports.hidden', {
                                count: imports.hidden,
                                total: countParam(imports.hidden),
                            })}
                        </Typography>
                    ) : null}
                    <ImportTotals isNarrow={isNarrow} totals={imports.totals} />
                </div>
            ) : (
                <Card
                    className="items-center p-8"
                    variant="secondary"
                    {...getNodeTestIdProp('onboarding-done-empty')}
                >
                    <Typography color="muted">
                        {t('onboarding.done.noImports')}
                    </Typography>
                </Card>
            )}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                    className="w-full sm:w-auto"
                    onPress={onImportMore}
                    variant="secondary"
                    {...getNodeTestIdProp('onboarding-done-import-more')}
                >
                    {t('onboarding.done.importMore')}
                </Button>
                <Button
                    className="w-full sm:w-auto"
                    onPress={onFinish}
                    {...getNodeTestIdProp('onboarding-done-finish')}
                >
                    {t('onboarding.done.finish')}
                </Button>
            </div>

            <Modal
                isOpen={isConfirmOpen}
                onOpenChange={(open) => !open && onCancelFinish()}
            >
                <Modal.Backdrop isDismissable={!isFinishing}>
                    <Modal.Container size="md">
                        <Modal.Dialog
                            className="w-full max-w-lg"
                            {...getNodeTestIdProp(
                                'onboarding-done-confirm-dialog',
                            )}
                        >
                            <Modal.CloseTrigger />
                            <Modal.Header>
                                <Modal.Heading>
                                    {t('onboarding.done.confirm.title')}
                                </Modal.Heading>
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-4">
                                <ul className="flex list-disc flex-col gap-1 ps-5 text-sm text-muted">
                                    <li>
                                        {t('onboarding.done.confirm.closes')}
                                    </li>
                                    <li>
                                        {t('onboarding.done.confirm.branches')}
                                    </li>
                                    <li>
                                        {t('onboarding.done.confirm.undoEnds')}
                                    </li>
                                </ul>
                                {finishError ? (
                                    <Alert
                                        status="danger"
                                        {...getNodeTestIdProp(
                                            'onboarding-done-confirm-error',
                                        )}
                                    >
                                        <Alert.Indicator />
                                        <Alert.Content>
                                            <Alert.Title>
                                                {t(
                                                    'onboarding.done.confirm.errorTitle',
                                                )}
                                            </Alert.Title>
                                            <Alert.Description>
                                                {finishError.message ||
                                                    t(
                                                        'onboarding.done.confirm.errorFallback',
                                                    )}
                                            </Alert.Description>
                                        </Alert.Content>
                                    </Alert>
                                ) : null}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button
                                    isDisabled={isFinishing}
                                    onPress={onCancelFinish}
                                    variant="tertiary"
                                    {...getNodeTestIdProp(
                                        'onboarding-done-confirm-cancel',
                                    )}
                                >
                                    {t('onboarding.done.confirm.cancel')}
                                </Button>
                                <Button
                                    isPending={isFinishing}
                                    onPress={onConfirmFinish}
                                    {...getNodeTestIdProp(
                                        'onboarding-done-confirm-finish',
                                    )}
                                >
                                    {finishError
                                        ? t('onboarding.done.confirm.retry')
                                        : t('onboarding.done.confirm.finish')}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal>
        </section>
    );
};
